import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpErrorResponse } from "@angular/common/http";
import { AppConfig } from '../../app.config';
import { context } from 'bookingbug-core-js';
import { environment } from '../../../environments/environment';
import { Observable, throwError as observableThrowError } from "rxjs";
import { catchError, map } from 'rxjs/operators';
import { DatastoreService } from "../datastore.service";

@Injectable({
  providedIn: "root",
})
export class ApiService {
  private jrniCore = AppConfig.settings.core;
  private core = AppConfig.settings.addressApi;
  private jrniEndpoint = AppConfig.settings.apiServer;
  // X-BL-TOKEN: api_access (eu-west-1) entry for Solihull registrars — restore the value from the Gen-1 api.service.ts (git tag backup/solihull-ceremonies-jrni-questions-2026-09-29)
  private token = '3fc8fdee3558979416b201a31a4c2f358542f53d0005971f429e1b8c097adddf';
  	public configCache: any = {};

  public apiServer;
  public blApiToken;
  public blApi;
  public frontSheetEmail;
  constructor(private http: HttpClient, private datastoreService: DatastoreService) {
    if (environment.production) {
      this.apiServer = 'https://api.bookinglab.co.uk';
      // Production clienttoken for solihull:37016 — restore from the Gen-1 api.service.ts (same tag)
      this.blApiToken = "2542690799b770899gk88ntc630yhyrwmcagix7guym0u381150q44q6zixkvkzi";
      this.blApi = "https://pnwnvb64o6.execute-api.eu-west-2.amazonaws.com/1";
      console.log("Production api");
      this.frontSheetEmail = "test@bookinglab.co.uk";
    } else {
      this.apiServer = 'https://staging.bookinglab.co.uk';
      // Staging clienttoken for solihull-staging:37016 — restore from the Gen-1 api.service.ts, or token-maker-staging (idempotent)
      this.blApiToken = "d78tz53zm0ufivdp7819s32o39tnmzp2dizb5l33q9s50hg4ahq224k9348d1161";
      this.blApi = "https://m5nius1kad.execute-api.eu-west-2.amazonaws.com/1";
      console.log("Staging api");
      this.frontSheetEmail = "test@bookinglab.co.uk";
    }
  }

  async addMetadataToBooking(companyId: number, bookingId: number, metadataType: string, data: any) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        clienttoken: `${this.blApiToken}`,
      };

      return await this.http.post(`${this.blApi}/company/${companyId}/booking/${bookingId}/meta/${metadataType}`, data, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  getAppConfig(): { blApiToken: string; apiBaseUrl: string; company_id: string; customer_name: string; } {
    return {
      blApiToken: this.blApiToken,
      apiBaseUrl: this.blApi,
      company_id: AppConfig.settings.core.company_id,
      customer_name: AppConfig.settings.env.customer_name,
    }
  }

  get(postcode) {
    const key = this.core.key;
    return this.http.get(
      `https://wcc-apim-external.azure-api.net/locations/v1/postcode?postcode=${postcode}&national=true`, { headers: { 'Ocp-Apim-Subscription-Key': '2986fe65a63e40898bd828c4c7d15bf4' } }
    );
  }

  async getExtraItemProperties(bookingQuestionCode, companyId, serviceId) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`
      };

      return await this.http.get(`${this.blApi}/company/${companyId}/extra-item/${bookingQuestionCode}/service/${serviceId}`, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async lookupReference(table: any, field: any, reference: any, referenceExtra: any = false, field_extra: any = false) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        clienttoken: `${this.blApiToken}`,
      };

      const bodyData = {
        client: `${AppConfig.settings.env.customer_name}`,
        reference: `${reference}`,
        table: `${table}`,
        field: `${field}`
      };

      if (referenceExtra && field_extra) {
        bodyData["referenceExtra"] = referenceExtra
        bodyData["field_extra"] = field_extra
      }

      return await this.http
        .post(`${this.blApi}/eligibility_reference_lookup`, bodyData, {
          headers,
        })
        .toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async getWeekOfBookings(address, company) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
    const body = {
      "host": `${context.apiUrl}`,
      "company_id": `${company.id}`,
      "limit": `${company.extra.booking_limit_days}`,
      "address": `${address}`,
    }

    return await this.http.post(`${this.apiServer}/weeklyLimit`, body, { headers }).toPromise();
  }

  async checkEmailNumber(email, mobile, company) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
    const body = {
      "host": `${context.apiUrl}`,
      "company_id": `${company.id}`,
      "email": email ? email : mobile,
      "searchType": "elastic"
    }

    return await this.http.post(`${this.apiServer}/findElasticClient`, body, { headers }).toPromise();
  }

  async getBookingsForUser(company, parent, email) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
    const body = {
      "host": `${context.apiUrl}`,
      "company_id": `${company.id}`,
      "parent_id": `${parent.id}`,
      "email": `${email}`,
    }

    return await this.http.post(`${this.apiServer}/findElasticClient`, body, { headers }).toPromise();
  }

  async checkForDoubleBooking(company, slot, email = null, mobile = null) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
    const body = {
      "host": `${context.apiUrl}`,
      "company_id": `${company.id}`,
      "current_date": slot,
    }

    if (email) {
      body["email"] = email;
    } else {
      body["mobile"] = mobile;
    }

    return this.http.post(`${this.apiServer}/unavailableTimes`, body, { headers }).toPromise();
  }

  async getTimes(
    company_id: number,
    service_id: number,
    start_date: string,
    end_date: string,
    resource_id: number,
    duration: number
  ) {
    try {
      const headers: HttpHeaders = new HttpHeaders({
        "App-Id": this.jrniCore.app_id,
        "App-Key": this.jrniCore.app_key,
      });

      const url = `${this.jrniEndpoint.api_url}/api/v5/${company_id}/times?only_available=true&service_id=${service_id}&start_date=${start_date}&end_date=${end_date}&duration=${duration}&resource_id=${resource_id}`;
      console.log(headers);
      console.log(url);
      return await this.http.get(url, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async checkSlotAvailability(company, slot) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
    const body = {
      "host": `${context.apiUrl}`,
      "company_id": `${company.id}`,
      "slot": slot,
    }

    return this.http.post(`${this.apiServer}/slotAvailability`, body, { headers }).toPromise();
  }

  getServiceByAdmin(parent_id, company, service_id, resource_id) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
    const body = {
      "host": `${context.apiUrl}`,
      parent_id: parent_id,
      service_id: service_id,
      "company_id": `${company.id}`,
      "resource_id": resource_id
    }
    return this.http.post(`${this.apiServer}/getServiceByAdmin`, body, { headers }).toPromise();

  }
  async findPurchase(company, purchaseId) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
    const body = {
      "host": `${context.apiUrl}`,
      "company_id": `${company.id}`,
      "purchase_id": purchaseId,
    }

    return this.http.post(`${this.apiServer}/findPurchase`, body, { headers }).toPromise();
  }

  async getService(company, service_id) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
    const body = {
      "host": `${context.apiUrl}`,
      "company_id": `${company.id}`,
      "service_id": service_id,
    }

    return this.http.post(`${this.apiServer}/getService`, body, { headers }).toPromise();
  }

  async getConfig(company: any = undefined) {
    // mini cache for the config
    if (Object.keys(this.configCache).length > 0) {
      console.log('Returning cached config');
      return this.configCache;
    }

    console.log('Fetching config for company:', company);
    const headers = {
      'Content-Type': 'application/json',
      clienttoken: `${this.blApiToken}`,
    };
    const body = {
      client: `${AppConfig.settings.env.customer_name}`,
      company: `${company}`,
    };

    const result = await this.http
      .post(`${this.blApi}/config`, body, { headers })
      .toPromise();

    this.configCache = result;
    return result;
  }

  async addNoteToBooking(company: any, bookingId: number, note: string) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
    const body = {
      "host": `${context.apiUrl}`,
      "company_id": `${company.id}`,
      "bookingId": `${bookingId}`,
      "note": `booking`
    }

    return await this.http.post(`${this.apiServer}/addNote`, body, { headers }).toPromise();
  }

  async updatePrice(company: any, bookingId: number, purchaseId: string, price: string, paid: string) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
    const body = {
      "host": `${context.apiUrl}`,
      "company_id": `${company.id}`,
      "booking_id": `${bookingId}`,
      "purchase_id": `${purchaseId}`,
      "price": `${price}`,
      "pay": `${paid}`
    }

    return await this.http.post(`${this.apiServer}/updatePrice`, body, { headers }).toPromise();
  }

  async sendFrontSheetEmail(booking) {
    try {
      const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
      const senderAddress = "no-reply@bookinglab.co.uk";
      let subject = "Registration Service - Ceremonies Front Sheet";
      const data: EmailType = {
        host: `${context.apiUrl}`,
        // Communication templates live on the grandparent company (37015), as in the Gen-1 app.
        companyid: '37015',
        slotid: booking.id,
        recipient: this.frontSheetEmail,
        type: 'frontsheet-email',
        senderAddress,
        subject
      }
      return await this.http.post(`${this.apiServer}/sendCommunicationEmail`, data, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async sendEmail(type, bookingData, recipient, company) {
    try {
      const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
      const senderAddress = "no-reply@bookinglab.co.uk";
      let subject = '';
      if (type === 'CANCEL') {
        subject = 'Registration Service - Booking cancelled'
      } else if (type === 'AMEND') {
        subject = 'Registration Service - Booking amended'
      } else if (type === 'MOVED') {
        subject = 'Registration Service - Booking moved'
      } else if (type === 'CONFIRMED') {
        subject = 'Registration Service - Booking Confirmation'
      }
      const data: EmailType = {
        host: `${context.apiUrl}`,
        companyid: '37015',
        slotid: bookingData.id,
        recipient: recipient,
        type: type,
        senderAddress,
        subject
      }
      console.log(data);
      return await this.http.post(`${this.apiServer}/sendCommunicationEmail`, data, { headers }).toPromise();
    }
    catch (err) {
      console.error(err);
      throw err;
    }
  }

  async resendConfirmation(company, bookingid: Number) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-company-id': `${company.id}`,
        'clienttoken': `${this.blApiToken}`
      };
      const body = {
      }
      return await this.http.put(`${this.blApi}/company/${company.id}/booking/${bookingid}/confirmation`, body, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async getDays(company_id: number, service_id: number, start_date: String, end_date: String, resource: any, duration: any) {
    try {
      const headers = { 'Content-Type': 'application/json', 'clienttoken': this.blApiToken };
      let url = `${this.blApi}/company/${company_id}/service/${service_id}/days/${start_date}/${end_date}`;
      let join = '?';
      if (resource) {
        url = `${url}${join}resources=${resource}`;
        join = '&';
      }
      if (duration) {
        url = `${url}${join}duration=${duration}`;
      }
      return await this.http.get(`${url}`, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async searchVenue(company, query) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'x-company-id': `${company.id}`,
        'clienttoken': `${this.blApiToken}`
      };
      return await this.http.get(`${this.blApi}/company/${company}/venue-lookup?search=${query.toLowerCase()}`, { headers }).toPromise();
    } catch (error) {
      // return (error as Error).message;
      return error.message;
    }
  }

  async generateSSOToken(companyId: any, first_name: any, last_name: any, email: any) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`
      };

      const bodyData = {
        first_name: `${first_name}`,
        last_name: `${last_name}`,
        email: `${email}`
      };

      return await this.http.post(`${this.blApi}/company/${companyId}/sso`, bodyData, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async ssoLogin(companyId: any, token: any) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`
      };

      const bodyData = {
        token: `${token}`
      };

      return await this.http.post(`${this.blApi}/company/${companyId}/sso/login`, bodyData, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async ssoLoginAdmin(companyId: any, token: any) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        clienttoken: `${this.blApiToken}`,
      };

      const bodyData = {
        token: `${token}`,
      };

      return await this.http
        .post(`${this.blApi}/company/${companyId}/sso/login/admin`, bodyData, {
          headers,
        })
        .toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async fees(companyId: any, date: string, serviceId: any, duration: number) {
    // return {
    //   "deposit": 100,
    //   "full_fee": 1143,
    //   "final_balance": 1043,
    //   "payment_code": "WCRG002",
    //   "out_of_hours": 0,
    //   "short_notice": 0,
    //   "full_vat_n": 0,
    //   "full_vat_s": 0,
    //   "dep_vat_n": 0,
    //   "dep_vat_s": 0,
    //   "bal_vat_n": 0,
    //   "bal_vat_s": 0,
    //   "is_bank_holiday": false,
    //   "duration": 60
    // }

    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`
      };

      const bodyData = {
        "service_id": serviceId,
        "date": `${date}`,
        "duration": duration
      };

      return await this.http.post(`${this.blApi}/company/${companyId}/fees`, bodyData, { headers }).toPromise();
    } catch (error) {
      // A 404 from /fees means "we could not price this", never a price. Do not
      // swallow it into a string: callers must see the failure, or they coerce
      // the missing fee to 0 and the customer checks out paying nothing.
      if (error instanceof HttpErrorResponse && error.status === 404) {
        console.warn(`fees: not priced for service ${serviceId} on ${date} —`, error.error?.message);
      }
      throw error;
    }
  }

  async linkExtraItem(companyId: any, serviceId: any, bookingId: any, itemId: string, extra: any, option?: string) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`
      };

      const bodyData = {
        "global_service_id": serviceId,
        "booking_id": `${bookingId}`,
        "item_id": `${itemId}`,
        "data": extra
      };

      let url = `${this.blApi}/company/${companyId}/extra-item/link`;
      if (option) {
        url = `${url}?op=${option}`
      }

      return await this.http.post(url, bodyData, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async deleteExtraItemLink(companyId: any, serviceId: any, bookingId: any, itemId: string) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`
      };

      return await this.http.delete(`${this.blApi}/company/${companyId}/extra-item/link/${itemId}/${bookingId}`, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async getExtraItem(companyId: any, serviceId: any, itemId: string) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`
      };

      return await this.http.get(`${this.blApi}/company/${companyId}/extra-item/${itemId}/service/${serviceId}`, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async getServiceItemExtraLinksForBooking(companyId: number, bookingId: string, authToken: string, memberId: number) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`,
        'authtoken': `${authToken}`,
        'x-member-id': `${memberId}`
      };

      return await this.http.get(`${this.blApi}/company/${companyId}/extra-item/link/${bookingId}`, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async payPurchase(companyId: number, serviceId: number, purchaseId: number, purchaseData: any, clientId: number) {
    try {
      let authToken: any = false;
      if (localStorage.getItem('auth_token')) {
        authToken = localStorage.getItem('auth_token');
      }

      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`,
        'x-member-id': `${clientId}`
      };
      if (authToken) {
        headers['authtoken'] = `${authToken}`
      }

      return await this.http.put(`${this.blApi}/company/${companyId}/service/${serviceId}/purchase/${purchaseId}/pay`, purchaseData, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async sendWelcomeEmail(companyId: any, memberId: any) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`
      };

      let url = `${this.blApi}/company/${companyId}/member/${memberId}/send_welcome_email`;

      return await this.http.post(url, {}, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async bookingTimeline(companyId: number, purchaseid: any, memberid: any, memberEmail: any) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`
      };

      let url = `${this.blApi}/company/${companyId}/auditlog/interactions`;
      let body = {
        "data": {
          "email_type": "Welcome Email",
          "from": "notifications@bookinglab.co.uk",
          "to": memberEmail,
          "subject": "Welcome to Westminster - a day to remember",
          "email_body": "",
          "content_type": "text/html; charset=UTF-8"
        },
        "total_id": purchaseid,
        "client_id": memberid,
        "interaction_type": "email_sent"
      }

      return await this.http.post(url, body, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async submitCaseToOracle(company: any, booking: any) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
    const body = {
      "host": `${context.apiUrl}`,
      "company_id": `${company.id}`,
      "booking_id": booking.id,
      "purchase_id": booking.purchase_id
    }

    return await this.http.post(`${this.apiServer}/oracle/recordIncident`, body, { headers }).toPromise();
  }

  createBasket(company: any): Observable<any> {
    let authToken: any = false;
    if (localStorage.getItem('auth_token')) {
      authToken = localStorage.getItem('auth_token');
    }
    const headers = {
      'Content-Type': 'application/json',
      'clienttoken': `${this.blApiToken}`,
      'x-company-id': company.id
    };
    if (authToken) {
      headers['authtoken'] = authToken;
    }

    const url = `${this.blApi}/basket-public`;

    return this.http.post<any>(url, { "company_id": company.id }, { headers, observe: 'response' }).pipe(map(data => data), catchError(this.handleError));

  }

  /**
   * Resolve the JRNI resource_id for a basket call.
   *
   * datastoreService.selectedSuggestion.resource_id is the source of truth when set;
   * otherwise fall back to the candidates the caller supplied.
   * Callers have historically passed the whole resource OBJECT rather than its id, and
   * parseInt(object) is NaN — which serialises to null, and JRNI then silently assigns
   * whatever resource is free at that slot, booking the customer into the wrong room.
   * Accept an id or an object for each candidate, take the first usable one, and return
   * null (never NaN, never 0) when there is genuinely no resource to send.
   */
  public resolveResourceId(...candidates: any[]): number | null {
    const suggested = Number(this.datastoreService.selectedSuggestion?.resource_id);
    if (suggested > 0) {
      return suggested;
    }
    for (const candidate of candidates) {
      const id = Number(candidate && typeof candidate === 'object' ? candidate.id : candidate);
      if (id > 0) {
        return id;
      }
    }
    return null;
  }

  async addServiceItem(company: any, basket: any, startTime: string, service: any, questions: any[], duration?: number, resource?: any) {
    try {
      console.log('auth token to use:', localStorage.getItem('auth_token'));
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`,
        'x-company-id': company.id,
        'authtoken': `${localStorage.getItem('auth_token')}`
      };

      const url = `${this.blApi}/baskets/${basket.id}/service_item-public`;

      const serviceItem: any = {
        "company_id": company.id,
        "service_id": service.id,
        "start": `${startTime}`,
        "questions": questions,
        "resource_id": this.resolveResourceId(resource),
      };
      if (duration) {
        serviceItem.duration = duration;
      }
      return await this.http.post(url, serviceItem, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }

  }

  async updateServiceItem(company: any, basket: any, serviceItem: any, body) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`,
        'x-company-id': company.id,
        'authtoken': `${localStorage.getItem('auth_token')}`
      };

      const url = `${this.blApi}/baskets/${basket.id}/service_item-public/${serviceItem.id}`;

      return await this.http.put(url, body, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }

  }

  async deleteServiceItem(company: any, basket: any, serviceItem: any) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`,
        'x-company-id': company.id,
        'authtoken': `${localStorage.getItem('auth_token')}`
      };

      const url = `${this.blApi}/baskets/${basket.id}/service_item-public/${serviceItem.id}`;

      return await this.http.delete(url, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }

  }

  async deleteBaskets(company: any) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`,
        'x-company-id': company.id,
        'authtoken': `${localStorage.getItem('auth_token')}`
      };

      const url = `${this.blApi}/basket-public`;

      return await this.http.delete(url, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }

  }

  async basketCheckout(company: any, basket: any, client: any) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`,
        'x-company-id': company.id,
        'authtoken': `${localStorage.getItem('auth_token')}`
      };

      const url = `${this.blApi}/baskets/${basket.id}/public-checkout`;

      return await this.http.post(url, {
        company_id: company.id,
        client,
        no_notifications: true,
        settings: {
          status: "booked",
          "firstAvailableSlot": this.datastoreService.firstSlotAnalytic ? this.datastoreService.firstSlotAnalytic : "Null"
        }
      }, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async getAdministrators(company: any) {
    try {
      const headers = {
        'Content-Type': 'application/json',
        'clienttoken': `${this.blApiToken}`,
        'x-company-id': company.id,
        'authtoken': `${localStorage.getItem('auth_token')}`
      };

      const url = `${this.blApi}/admin/login`;

      return await this.http.get(url, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  // ---- Ported from the Gen-1 Solihull app: post-payment paperwork + failure logging ----

  async sendAdminEmail(booking, ceremonies_admin_inbox) {
    try {
      const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
      const senderAddress = "notifications@bookinglab.co.uk";
      const subject = "Registration Service - Booking Made";
      const data: EmailType = {
        host: `${context.apiUrl}`,
        companyid: '37015',
        slotid: booking.id,
        recipient: ceremonies_admin_inbox,
        type: 'booking-confirmation-admin',
        senderAddress,
        subject
      }
      return await this.http.post(`${this.apiServer}/sendCommunicationEmail`, data, { headers }).toPromise();
    } catch (error) {
      return (error as Error).message;
    }
  }

  async getIpAddress() {
    return this.http.get('https://api.ipify.org?format=json').toPromise();
  }

  async logPaymentExtra(company, service, bookingId, extraObj) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };

    const clientAdrr = await this.getIpAddress();

    const body = {
      "host": `${context.apiUrl}`,
      "company_id": `${company.id}`,
      "service_id": `${service.id}`,
      "order_id": `${bookingId}`,
      "provider": "capita",
      "client_addr": `${clientAdrr['ip']}`,
      "extra_obj": JSON.stringify(extraObj),
      "type": "extra"
    };
    return this.http.post(`${this.apiServer}/logPayment`, body, { headers }).toPromise();
  }

  async cancelMultiBookings(company_id, booking_id) {
    const headers = { 'Content-Type': 'application/json', 'X-BL-TOKEN': `${this.token}` };
    const body = {
      "host": `${context.apiUrl}`,
      "company_id": `${company_id}`,
      "booking_id": `${booking_id}`,
    }

    return await this.http.post(`${this.apiServer}/cancelMultiBookings`, body, { headers }).toPromise();
  }

  private handleError(res: HttpErrorResponse | any) {
    console.error(res.error || res.body.error);
    return observableThrowError(res.error || 'Server error');
  }

}

export class EmailType {
  host: String;
  companyid: String;
  slotid: String;
  recipient: String;
  type: String = 'CANCEL' || 'AMEND' || 'MOVED' || 'CONFIRMED';
  senderAddress: String;
  subject: String;
};
